/**
 * @description 小四大store逻辑
 */
import React from 'react'
import { action, observable, toJS } from 'mobx'
import dayjs from 'dayjs'
import { message, Modal } from 'hljd'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { getMerchantInfo } from '@util'
import Api from '../api'
import { isChange } from '../utils'
import CheckImg from './checkImg'

class Store {
  constructor() {
    this.checkImg = new CheckImg()
  }

  @observable merchant_info = getMerchantInfo() //店铺信息
  @observable case_detail = {} //作品详情
  @observable show_form = false //表单可见
  @observable is_add_form = true // true：增加案例表单 false：编辑案例表单
  @observable property_id = ''
  @observable draftId = '' // 草稿id
  @observable isChanged = false // 记录数据是否改变
  @observable tagList = [] //案例标签集合
  @observable form_init = {} //出事表单
  /**
   * @title 违规发布限制提示
   */
  @action
  async getMercantPublicInfo() {
    const { data, success } = await Api.getMercantPublicInfo()
    if (success) {
      if (data?.leftChance === 0 && data?.onBlackList) {
        Modal.warning({
          title: `因违规截至 ${dayjs(data?.blackTimeEnd || Date.now()).format(
            'YYYY年MM月DD日 HH:mm:ss'
          )} 前每日限制发布案例${data?.dailyChance}次，今天还可发布${
            data?.leftChance
          }次`
        })
      }
    }
  }

  // 通用修改store方法
  @action
  setStoreData = (type, value) => {
    this[type] = value
  }

  /**
   * @title 获取案例详情
   */
  @action
  getCaseDetail = async id => {
    const { data, success } = await Api.getCase({
      id
    })
    if (success) {
      data.emceeMemberId = data?.emceeMember?.id
      this.case_detail = data
    }
  }

  /**
   * @title 获取案例草稿详情
   */
  @action
  getDraftDetail = async id => {
    const { data, success } = await Api.getDraftDetail({
      id
    })
    if (success) {
      // 这个接口老逻辑，中间有一层content，不然可以和上面的方法合并在一起
      this.case_detail = data.content
    }
  }

  /**
   * @title 删除案例或案例草稿
   */
  @action
  deleteCase = async (id, isDraft) => {
    this.forceLeave = true
    let res
    if (isDraft) {
      res = await Api.delDraft({
        id
      })
    } else {
      res = await Api.deleteSetMeal({
        id
      })
    }

    const { success, msg } = res
    if (success) {
      window.location.hash = isDraft
        ? '#/localMerchant/case/draftList'
        : '#/localMerchant/case'
    } else {
      Modal.warning({
        title: msg,
        icon: (
          <ExclamationCircleFilled
            style={{
              color: '#ffbf00'
            }}
          />
        )
      })
    }
  }

  /**
   * @title 检查文字是否存在敏感词
   */
  @action
  checkWord = async (content, type = 2) => {
    if (!content || !content.trim()) {
      return {
        code: 0
      }
    }
    const { data } = await Api.checkWord({
      content,
      type
    })
    return data
  }

  // 作品标签相关
  /**
   * @title 获取作品标签
   */
  @action
  getMealMarks = async () => {
    const { data = [], success } = await Api.getMealMarks({
      property_id: this.property_id,
      commodity_type: 1
    })
    if (success) {
      this.tagList = []
      // 此处需求要将原来多行展示的标签数据全部一行展示，因此需要对数据做一次处理，均处理至一层数组中
      data.forEach(({ children = [] }) => {
        this.tagList = this.tagList.concat(children)
      })
    }
  }
  // 修改作品标签选中状态
  @action
  changeTagValue = (i, flag) => {
    const tempList = [...this.tagList]
    if (!flag) {
      tempList[i].isActive = false
      this.tagList = toJS(tempList)
      return
    }
    // // 已active的tag数量
    tempList[i].isActive = true
    this.tagList = toJS(tempList)
  }

  /**
   * @title 初始化案例标签并回填选中标签  TODO 老方法，有时间优化下
   */
  initTagList = tagIds => {
    if (!tagIds || tagIds.length == 0) {
      return
    }
    const tempTags = [...this.tagList]
    for (let i = 0; i < tempTags.length; i++) {
      if (tagIds.indexOf(tempTags[i].mark_id) > -1) {
        tempTags[i].isActive = true
      }
    }

    this.tagList = [...tempTags]
  }
  @observable forceLeave = false
  @observable submitting = false // 和 show_form 相同作用，都是为了控制整个页面是否处于loading状态，可合并为一个值进行控制
  // 提交表单
  @action
  submitForm = async (value, type, _isChanged) => {
    // 校验图片 是否非rgb的图片
    if (
      !(await this.checkImg.beforeAction({
        types: [2, 3]
      }))
    ) {
      this.submitting = false
      return
    }
    // 案例详细信息 内容 提交时数据处理
    const { setMealDetail, emceeMemberId } = value
    const setMeal = {
      title: value.title, // 案例标题
      coverPath: setMealDetail ? setMealDetail[0]?.content.coverPath : ''
      // 封面图
    }
    const tagList = []
    for (let i = 0; i < this.tagList.length; i++) {
      if (this.tagList[i].isActive) {
        tagList.push(this.tagList[i].mark_id)
      }
    }
    const params = {
      emceeMemberId,
      propertyId: value.property_id ?? this.merchant_info.property_id,
      setMealDetail, // 案例正文
      setMeal,
      tagList: tagList?.join(',') ?? undefined
    }

    // 保存为草稿
    if (type == 'draft') {
      await this.saveDraft(params)
      return
    }
    // 如果是通过草稿发布，需要加上id和type
    if (this.draftId) {
      params.draftId = this.draftId
      params.draftType = 'case'
    }

    // 如果是新增案例
    if (this.is_add_form) {
      const { success, data } = await Api.editCase(params)
      this.forceLeave = true
      this.submitting = false
      if (success) {
        return data
      }
    } else {
      // 编辑案例
      params['id'] = this.case_detail.id
      if (
        !isChange(
          tagList,
          this.case_detail,
          value.setMealDetail,
          emceeMemberId,
          this.isChanged
        )
      ) {
        message.warning('未修改任何信息，无需提交审核')
        this.submitting = false
        return false
      }

      this.submitting = false
      this.forceLeave = true
      const { success, data } = await Api.editCase(params)
      this.submitting = false
      if (success) {
        this.forceLeave = true
        return data
      }
    }
  }

  @observable needReload = true
  saveDraft = async value => {
    if (this.draftId) {
      value.id = this.draftId
    }
    const { success, data } = await Api.saveDraft(value)
    this.forceLeave = true
    if (success) {
      message.success('提交成功')
      if (this.needReload) {
        // 刷新页面
        window.location.href = `#/localMerchant/case/add?propertyId=${
          this.property_id
        }&draftId=${data}`
        window.location.reload()
      }
    }
    this.submitting = false
  }

  // 初始化新增表单
  @action
  initAddForm = () => {
    this.show_form = true
  }

  /**
   * @title 若是编辑或草稿情况下，获取接口数据后，将获取到的数据回填表单，需要去除一些没有用的逻辑
   */
  @action
  initUpdateForm = async () => {
    this.show_form = true
    // 初始化回填案例标签，后续考虑案例标签是否需要纳入表单中,优化原来一连串的无用方法
    const tagIds = (this.case_detail.tags || []).map(item => item.markId)
    this.initTagList(tagIds)
    this.form_init = {
      ...this.case_detail
    }
  }
  /**
   * @title 初始化页面数据，接口请求
   */
  @action
  initPage = async (id, propertyId, draftId = '') => {
    this.property_id = propertyId ? propertyId : this.merchant_info.property_id

    await Promise.all([this.getMealMarks(), this.getMercantPublicInfo()])

    // 草稿箱进入
    if (draftId) {
      await this.getDraftDetail(draftId)
      // 草稿模式下也属于新增案例类型
      this.is_add_form = true
      this.draftId = draftId
      this.initUpdateForm()
      return
    }
    // 非草稿箱的编辑下进入
    if (id) {
      await this.getCaseDetail(id)
      this.is_add_form = false
      this.initUpdateForm()
    } else {
      // 新增案例
      this.initAddForm()
    }
  }

  // 新增成员字段相关方法
  @observable memberList = []
  setMemberList = value => {
    this.memberList = value
  }
}

export default Store
